/** @jsxImportSource @emotion/react */
import React from "react";
import { Col, Form, Modal, Row, Button, Select, InputNumber } from "antd";
import { useForm, Controller } from "react-hook-form";
import * as Styles from "./styles";

export interface FilterParams {
  keyword?: string;
  tenant_id?: string;
  range?: string;
  storey?: string;
  numberOfBed?: number;
  numberOfBath?: number;
  numberOfGarage?: number;
  fromPrice?: number;
  toPrice?: number;
  lotSizeFrom?: number;
  lotSizeTo?: number;
}

interface ModalHomeFilterProps {
  onCancel: () => void;
  onSearch: (filter: FilterParams) => void;
  visible: boolean;
  filter: FilterParams;
}

const ModalHomeFilterComponent: React.FC<ModalHomeFilterProps> = ({
  visible,
  onCancel,
  onSearch,
  filter,
}: ModalHomeFilterProps) => {
  const { handleSubmit, control, reset } = useForm<FilterParams>({
    mode: "onSubmit",
    defaultValues: filter,
  });

  const handleSubmitHandle = React.useCallback(
    async (data: FilterParams) => {
      const dataObject = data as Record<string, string | undefined>;
      for (const key in data) {
        if (!dataObject[key]) {
          delete dataObject[key];
        }
      }
      onSearch(dataObject as FilterParams);
    },
    [onSearch],
  );

  const onCancelHandle = React.useCallback(() => {
    reset();
    onCancel();
  }, [onCancel, reset]);

  return (
    <Modal
      title="Filter"
      visible={visible}
      closable={false}
      footer={[
        <Button key="back" size="large" onClick={onCancelHandle}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          size="large"
          onClick={handleSubmit(handleSubmitHandle)}
        >
          Search
        </Button>,
      ]}
    >
      <Form layout="vertical" onFinish={handleSubmit(handleSubmitHandle)}>
        <Row gutter={16}>
          <Col xs={8}>
            <Form.Item label="Range" css={Styles.labelStyle}>
              <Controller
                name="range"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select {...field} size="large">
                    <Select.Option value="">Any</Select.Option>
                    <Select.Option value="range1">Range 1</Select.Option>
                    <Select.Option value="range2">Range 2</Select.Option>
                  </Select>
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item label="Storey" css={Styles.labelStyle}>
              <Controller
                name="storey"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select {...field} size="large">
                    <Select.Option value="">Any</Select.Option>
                    <Select.Option value="single">Single</Select.Option>
                    <Select.Option value="double">Double</Select.Option>
                  </Select>
                )}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={8}>
            <Form.Item label="Number of Bed" css={Styles.labelStyle}>
              <Controller
                name="numberOfBed"
                control={control}
                render={({ field }) => <InputNumber {...field} size="large" />}
              />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item label="Number of Bath" css={Styles.labelStyle}>
              <Controller
                name="numberOfBath"
                control={control}
                render={({ field }) => <InputNumber {...field} size="large" />}
              />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item label="Number of Garage" css={Styles.labelStyle}>
              <Controller
                name="numberOfGarage"
                control={control}
                render={({ field }) => <InputNumber {...field} size="large" />}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={8}>
            <Form.Item label="From Price" css={Styles.labelStyle}>
              <Controller
                name="fromPrice"
                control={control}
                render={({ field }) => <InputNumber {...field} size="large" />}
              />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item label="To Price" css={Styles.labelStyle}>
              <Controller
                name="toPrice"
                control={control}
                render={({ field }) => <InputNumber {...field} size="large" />}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={8}>
            <Form.Item label="Lot Size From" css={Styles.labelStyle}>
              <Controller
                name="lotSizeFrom"
                control={control}
                render={({ field }) => <InputNumber {...field} size="large" />}
              />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item label="Lot Size To" css={Styles.labelStyle}>
              <Controller
                name="lotSizeTo"
                control={control}
                render={({ field }) => <InputNumber {...field} size="large" />}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export const ModalHomeFilter = React.memo(ModalHomeFilterComponent);
